/**
 * AdjustTimeForm — 022-checkin-checkout-times
 *
 * Small inline form to adjust either the scheduled check-in or check-out time
 * for a booking. Rendered inside the booking detail expansion row.
 *
 * Props:
 *   field       — 'checkIn' | 'checkOut'
 *   booking     — the booking being edited (used to pre-fill the current time)
 *   onSuccess   — called with a feedback message after a successful save
 *   onError     — called with an error message on failure
 *   onClose     — hides the form
 *
 * After submit:
 * - HTTP 200  → onSuccess('pages:timeChangeApplied') + onClose
 * - HTTP 202  → onSuccess('pages:timePendingApproval') + onClose
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdjustCheckInTime, useAdjustCheckOutTime } from '../hooks/useBookings';
import { Booking } from '../types/booking';

interface AdjustTimeFormProps {
  field: 'checkIn' | 'checkOut';
  booking: Booking;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
}

export const AdjustTimeForm = ({
  field,
  booking,
  onSuccess,
  onError,
  onClose,
}: AdjustTimeFormProps) => {
  const { t } = useTranslation(['pages', 'forms', 'common', 'errors']);

  const currentTime =
    field === 'checkIn' ? booking.scheduledCheckInTime : booking.scheduledCheckOutTime;

  const [timeValue, setTimeValue] = useState<string>(
    currentTime ?? ''
  );

  const adjustCheckIn  = useAdjustCheckInTime();
  const adjustCheckOut = useAdjustCheckOutTime();

  const isPending = adjustCheckIn.isPending || adjustCheckOut.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!timeValue) return; // empty is not allowed here (input is required)

    try {
      if (field === 'checkIn') {
        await adjustCheckIn.mutateAsync({
          id: booking.bookingId,
          data: { newTime: timeValue },
        });
        onSuccess(t('pages:timeChangeApplied'));
      } else {
        const result = await adjustCheckOut.mutateAsync({
          id: booking.bookingId,
          data: { newTime: timeValue },
        });
        onSuccess(
          result.type === 'pending'
            ? t('pages:timePendingApproval')
            : t('pages:timeChangeApplied')
        );
      }
      onClose();
    } catch {
      onError(t('errors:somethingWentWrong'));
    }
  };

  const label =
    field === 'checkIn'
      ? t('forms:newCheckInTime')
      : t('forms:newCheckOutTime');

  const title =
    field === 'checkIn'
      ? t('pages:adjustCheckInTime')
      : t('pages:adjustCheckOutTime');

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-wrap items-end gap-3 mt-2"
      aria-label={title}
    >
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {label}
          <span className="ml-1 text-xs text-gray-400 font-normal">
            {t('pages:utcLabel')}
          </span>
        </label>
        <input
          type="time"
          value={timeValue}
          onChange={e => setTimeValue(e.target.value)}
          required
          className="input text-sm"
          aria-label={label}
        />
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isPending || !timeValue}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {isPending ? t('pages:adjustTimeSaving') : t('pages:adjustTimeSave')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {t('common:cancel')}
        </button>
      </div>
    </form>
  );
};
